import { isPromiseLike } from 'retil-support';
import { observe } from './observe';
import { hasSnapshot } from './source';
var FuseEffect = Symbol();
var throwArg = function (error) {
    throw error;
};
export function fuse(fusor) {
    var onNext = null;
    var onError = throwArg;
    var onClear;
    var isFusing = false;
    var isInEffect = false;
    var isInvalidated = false;
    var usedSubscribes = new Set();
    var usedUnsubscribes = new Map();
    var effectQueue = [];
    var effect = function (callback) {
        effectQueue.push(callback);
        return FuseEffect;
    };
    var use = function (source) {
        var defaultValues = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            defaultValues[_i - 1] = arguments[_i];
        }
        var core = source[0], select = source[1];
        var subscribe = core[1];
        usedSubscribes.add(subscribe);
        if (!usedUnsubscribes.has(subscribe)) {
            usedUnsubscribes.set(subscribe, subscribe(invalidate));
        }
        return defaultValues.length === 0 || hasSnapshot(source)
            ? select(core)
            : defaultValues[0];
    };
    var invalidate = function () {
        isInvalidated = true;
        if (!isInEffect && !isFusing) {
            runFusor();
        }
    };
    var unsubscribeFromUnused = function () {
        for (var _i = 0, _a = Array.from(usedUnsubscribes.entries()); _i < _a.length; _i++) {
            var _b = _a[_i], subscribe = _b[0], unsubscribe = _b[1];
            if (!usedSubscribes.has(subscribe)) {
                usedUnsubscribes.delete(subscribe);
                unsubscribe();
            }
        }
    };
    var runEffects = function () {
        var result;
        isInEffect = true;
        var finishEffect = function () {
            isInEffect = false;
            if (isInvalidated) {
                runFusor();
            }
            else {
                unsubscribeFromUnused();
            }
            return result;
        };
        // Use an act to clear the result if the effects don't finish immediately,
        // and to keep the subscription open.
        act(function () {
            var _loop_1 = function () {
                var effect_1 = effectQueue.shift();
                var effectResult = effect_1();
                result =
                    isPromiseLike(result) || isPromiseLike(effectResult)
                        ? Promise.resolve(result).then(function () { return effectResult; })
                        : effectResult;
            };
            while (effectQueue.length) {
                _loop_1();
            }
            return isPromiseLike(result)
                ? result.then(finishEffect, onError)
                : finishEffect();
        });
    };
    var runFusor = function () {
        if (!onNext || !onClear) {
            throw new Error('Retil error');
        }
        try {
            usedSubscribes.clear();
            isInvalidated = false;
            isFusing = true;
            var snapshot = fusor(use, effect);
            isFusing = false;
            if (usedSubscribes.size === 0) {
                throw new Error("not using any sources doesn't make any sense.");
            }
            if (snapshot === FuseEffect) {
                runEffects();
            }
            else if (isInvalidated && effectQueue.length === 0) {
                runFusor();
            }
            else {
                onNext(snapshot);
                if (effectQueue.length) {
                    runEffects();
                }
                else {
                    unsubscribeFromUnused();
                }
            }
        }
        catch (errorOrPromise) {
            isFusing = false;
            if (isPromiseLike(errorOrPromise)) {
                onClear();
                isInvalidated = true;
                errorOrPromise.then(function () {
                    // It's possible a source update has caused a run to complete in the
                    // intervening time.
                    if (isInvalidated && !isInEffect) {
                        runFusor();
                    }
                }, onError);
            }
            else {
                onError(errorOrPromise);
            }
        }
    };
    var source = observe(function (next, error, _, clear) {
        onNext = next;
        onError = error;
        onClear = clear;
        invalidate();
        return function () {
            onNext = null;
            onError = throwArg;
            onClear = null;
            for (var _i = 0, _a = Array.from(usedUnsubscribes.values()); _i < _a.length; _i++) {
                var unsubscribe = _a[_i];
                unsubscribe();
            }
            usedUnsubscribes.clear();
            usedSubscribes.clear();
        };
    });
    var act = source[2];
    return source;
}
